import type { AuthConsents, SuccessType } from '@/features/auth/types';

import Cookies from 'js-cookie';

import { useAuthTracking } from '@/features/auth/tracking';
import { AccountModes, AUTH_CONSENTS_KEY } from '@/features/auth/types';

import { captureMessageForSentry } from '@/libraries/sentry';
import { currentUser } from '@/helpers/user-helper';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { toastError } from '@/helpers/vfm/toast-helper';

import { LoginMessageModal } from '@/lazy-load-components';

import { useUserStore } from '@/stores/pinia/useUserStore';

export function useLoginFlow() {
	const userStore = useUserStore();

	const { t } = useI18n();
	const { trackAuth } = useAuthTracking();

	async function authenticateUser() {
		const user = await currentUser();
		if (!user) return;

		try {
			await userStore.signInInternally();
			userStore.fetchUserStats();
			userStore.fetchUserProfile();

			const storedConsents = sessionStorage.getItem(AUTH_CONSENTS_KEY);
			const authConsents = JSON.parse(storedConsents ?? 'null') as AuthConsents | null;

			trackAuth('login', {
				spec: {
					property: authConsents?.location
						? `${userStore.info.loginProvider}_${authConsents.location}`
						: userStore.info.loginProvider!,
					label: userStore.jwLoginId!,
				},
			});

			sessionStorage.removeItem(AUTH_CONSENTS_KEY);
			openSuccessModal('logged-in');
		} catch (error: any) {
			// If we can't sign in with Firebase SDK then we logout the user (jw-app state)
			await userStore.logout();

			trackAuth('loginError', {
				spec: {
					property: userStore.lastUsedLoginProvider ?? 'unknown',
					label: error.message.toString(),
				},
			});

			captureMessageForSentry(
				'[Authentication]: Login failed',
				{ error, where: '[useLoginFlow]: authenticateUser()' },
				'error',
				{ AuthFail: userStore.info.loginProvider }
			);

			openSuccessModal('login-failed');
		}
	}

	async function signUpUser() {
		try {
			await userStore.registerInternally();
			await userStore.fetchUserStats();
			await userStore.fetchUserProfile();

			const storedConsents = sessionStorage.getItem(AUTH_CONSENTS_KEY);
			const authConsents = JSON.parse(storedConsents ?? 'null') as AuthConsents | null;

			if (authConsents) {
				// If the user creates account with the SIGN_IN modal,
				// we want to save the user consents as true and assume they gave consent
				await userStore.saveSettings({
					jwConsents: {
						...userStore.settingsJWConsents,
						emails: authConsents.mode === AccountModes.SIGN_UP ? authConsents.acceptedEmailAgreement : true,
						termsAndConditions:
							authConsents.mode === AccountModes.SIGN_UP ? authConsents.acceptedPrivacyPolicy : true,
					},
				});

				trackAuth('dataProtectionSubmitted');

				const acceptEmails =
					authConsents.acceptedEmailAgreement == true || authConsents.mode === AccountModes.SIGN_IN ? 1 : 0;
				trackAuth('interestEmailsSubmitted', { spec: { value: acceptEmails } });

				trackAuth('signUp', {
					spec: {
						property: authConsents?.location
							? `${userStore.info.loginProvider}_${authConsents.location}`
							: userStore.info.loginProvider!,
						label: userStore.jwLoginId!,
					},
				});

				sessionStorage.removeItem(AUTH_CONSENTS_KEY);
			}

			openSuccessModal('logged-in');
		} catch (err) {
			// If the registration in consumer-api fails,
			// we just want to delete the user in firebase and then show a failure message.
			await userStore.deleteAccountPermanently();

			openSuccessModal('sign-up-failed');
			trackAuth('signUpError', {
				spec: {
					property: userStore.info.loginProvider ?? 'unknown',
					label: (err as Error).message,
				},
			});
		}
	}

	async function initLoginFlow() {
		if (!import.meta.client) return;
		if (userStore.isLoggedIn) return;

		// Check if access_token cookie exists first
		// if Cookie exists => cross-subdomain navigation with active session, proceed with auth
		const accessTokenCookie = Cookies.get('access_token');

		// Check sessionStorage to distinguish between the two
		// if sessionStorage has consents => fresh login
		const authConsentsStr = sessionStorage.getItem(AUTH_CONSENTS_KEY);

		if (!accessTokenCookie && !authConsentsStr) return;

		const user = await currentUser();

		const hasAuthMismatch = !userStore.isLoggedIn && user != null;
		if (!hasAuthMismatch) return;

		// Firebase user exists but our app state says logged out
		// Determine if this is a fresh login or a stale Firebase session
		const storeHasAuth = userStore.jwLoginId && userStore.accessToken != null;

		if (!storeHasAuth && !accessTokenCookie && !authConsentsStr) {
			return userStore.logout();
		}

		const context = useNuxtApp();
		const { claims } = await user.getIdTokenResult();
		if (claims.registration_error || !claims.jw_login_id) await signUpUser();
		else await authenticateUser();

		if (context?.$showRefreshTokenError) {
			context.provide('showRefreshTokenError', false);
			toastError({ message: t('WEBAPP_REFRESH_TOKEN_ERROR'), duration: 4000 });
		}
	}

	return {
		initLoginFlow,
	};
}

function openSuccessModal(successType: SuccessType) {
	try {
		ModalHelper.openModal(LoginMessageModal, { successType, closeDelay: 2000 }, { id: 'login-message-modal' });
	} catch (error: any) {
		captureMessageForSentry(
			'[Opening Login Message Modal]:failed',
			{ error, where: '[useLoginFlow]: openSuccessModal()' },
			'error'
		);
	}
}
